/**
 * Use case:
 * <FormItem>
 *  {getFieldDecorator('folderId', {
 *    initialValue: 11,
 *  })(
 *    <PathDirectorySelect />
 *  )}
 * </FormItem>
 */
import * as React from "react";
import { observer } from "mobx-react";
import { API } from "General/API";
import { projectsDataStore } from "Stores/ProjectsStore";
import { TreeSelectProps } from "antd/lib/tree-select";
import { AsyncTreeSelect } from "Components/LibHelper/AsyncTreeSelect";
import debounce from "debounce";
import { API_URL } from "General/APIEndpoints";
import { Spin } from "Components/General/Spin";
import { Tooltip } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { tenderCallDocumentsStore } from "Pages/Project/Tender/Stores/TenderCallDocumentsStore";

export interface PathDirectorySelectProps extends WithTranslation {
  treeSelectProps?: TreeSelectProps<string>;
  // controlled by "getFieldDecorator" https://ant.design/components/form/#getFieldDecorator(id,-options)-parameters
  value?: number;
  module?: API_MODULEDATA_MODULE | "LABELS_CALCULATOR";
  id?: string;
  fileId?: number;
  onChange?: (value: number) => void;
  initialTreeData?: API_TreeElement;
  allowClear?: boolean;
  projectIdContext?: number; // to fetch folders from different project
}

interface PathDirectorySelectState {
  folderId: number;
  data: TreeSelectProps<string>["treeData"];
  loading: boolean;
}

export const PathDirectorySelect = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<PathDirectorySelectProps, PathDirectorySelectState> {
      // @observable data: API_TreeElement[] = [];
      fetchDebounce;

      constructor(props: PathDirectorySelectProps) {
        super(props);
        this.state = {
          folderId: undefined,
          data: [],
          loading: false,
        };
        this.fetchDebounce = debounce(this.fetch, 500);
      }

      componentDidMount() {
        this.fetchDebounce(this.props.value);
      }

      componentDidUpdate(prevProps: PathDirectorySelectProps) {
        if (prevProps.projectIdContext !== this.props.projectIdContext) {
          this.fetch();
          this.setState({ folderId: undefined });
        }

        // sets the value if it was not set on componentDidMount
        if (!prevProps.value && this.props.value !== this.state.folderId) {
          this.handleNumberChange(this.props.value);
          this.fetchDebounce(this.props.value);
        }
      }

      handleNumberChange = (value) => {
        const isEmpty = !value;
        const folderId = isEmpty ? value : parseFloat(value);

        if (!isEmpty && isNaN(folderId)) {
          return;
        }

        this.setState({ folderId });
        this.triggerChange(folderId);
      };

      triggerChange = (changedValue) => {
        // Should provide an event to pass value to Form.
        const { onChange } = this.props;
        if (onChange) {
          onChange(changedValue);
        }
      };

      fetch = async (folderId?: number) => {
        const projectId = this.props.projectIdContext ? this.props.projectIdContext : projectsDataStore.projectId;
        try {
          this.setState({ loading: true });
          let response: API_TreeElement;
          let url: string;
          if (folderId) {
            if (this.props.module === "TENDER_MODERN") {
              url = API_URL.TENDER_DOCUMENTS.readFoldersArea(
                projectId,
                tenderCallDocumentsStore.callId,
                tenderCallDocumentsStore.currentFolderId
              );
            } else {
              url = API_URL.FOLDERS.getFullPath(projectId, folderId);
            }
            response = await API.getData(url);
          } else {
            if (this.props.module === "LABELS_CALCULATOR" && this.props.initialTreeData) {
              response = this.props.initialTreeData;
            } else {
              url = API_URL.FOLDERS.getTreeRoot(projectId);
              response = await API.getData(url);
            }
          }
          const simpleModeTreeData = this.flattenTree(response);
          this.setState({ data: simpleModeTreeData, folderId });
        } catch (err) {
          this.setState({ data: [] });
          throw err;
        } finally {
          this.setState({ loading: false });
        }
      };

      renderTitle = (item: API_TreeElement) => {
        const { t } = this.props;
        return <Tooltip title={item.unselectable ? t("No write access") : null}>{item.name}</Tooltip>;
      };

      fetchFolderById = async (folderId: number) => {
        const projectId = this.props.projectIdContext ? this.props.projectIdContext : projectsDataStore.projectId;
        try {
          const folderFound = this.state.data.find((item) => item.pId === folderId);
          if (folderFound) {
            return;
          }
          const response = await API.getData(API_URL.FOLDERS.getSubfolders(projectId, folderId));
          const normalizedData = response.subfolders.map((item) => {
            const folder = {
              id: item.id,
              value: item.id.toString(),
              title: this.renderTitle(item),
              pId: folderId,
              isLeaf: !item.children,
              disabled: item.unselectable,
            };
            if (item.linkType === "LINKED_EXTERN") {
              folder.isLeaf = true;
              folder.disabled = true;
            }
            return folder;
          });

          this.setState({ data: this.state.data.concat(normalizedData) });
        } catch (err) {
          throw err;
        }
      };

      flattenTree = (tree: API_TreeElement) => {
        const buffer: TreeSelectProps<string>["treeData"] = [
          {
            id: tree.id,
            value: tree.id.toString(),
            title: tree.name,
            key: tree.id,
            disabled: tree.unselectable,
          },
        ];
        if (!tree.subfolders?.length) {
          buffer.forEach((element) => {
            element.isLeaf = true;
          });
          return buffer;
        }
        const flatten = (subfolders: API_TreeElement[], parentId) => {
          for (let idx = 0; idx < subfolders.length; idx++) {
            const subfolder = subfolders[idx];
            if (subfolder.linkType === "LINKED_EXTERN") {
              // per default not follow (extern or intern) linked folders
              subfolder.unselectable = true;
              subfolder.children = undefined;
            }
            buffer.push({
              id: subfolder.id,
              value: subfolder.id.toString(),
              pId: parentId,
              title: this.renderTitle(subfolder),
              isLeaf: !subfolder.children,
              disabled: subfolder.unselectable,
            });
            if (subfolder.subfolders && subfolder.subfolders.length) {
              flatten(subfolder.subfolders, subfolder.id);
            }
          }
        };
        flatten(tree.subfolders, tree.id);
        return buffer;
      };

      render() {
        const { treeSelectProps } = this.props;
        const { folderId, loading } = this.state;
        const extendedProps = folderId ? { value: `${folderId}`, treeDefaultExpandedKeys: [`${folderId}`] } : null;

        return (
          <Spin spinning={loading}>
            <AsyncTreeSelect<string>
              allowClear={this.props.allowClear}
              treeData={this.state.data}
              handleSelect={this.handleNumberChange}
              loadData={(prop: { id: number }) => this.fetchFolderById(prop.id)}
              treeSelectProps={{ ...treeSelectProps, ...extendedProps }}
            />
          </Spin>
        );
      }
    }
  )
);
